import {noop} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {InfoBubblePillVariantsEnum} from '../../../../../libs/shared/ui/src/components/tip/infoBubblePill';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {EducationTip} from '../../../educationTips/educationTip';
import {generateStepText} from '../../welcome/onboardingWelcomeHelpers';
import {SandboxEducationTipProps} from './sandboxEducationTip';

/*
 * Style.
 */

const StyledInlineLinkButton = styled(InlineLinkButton)`
  font-weight: ${fontWeights.semibold};
`;

const StyledExternalIcon = styled(Icon)`
  display: inline;
  vertical-align: text-bottom;
`;

/*
 * Component.
 */

const endDemoActionLabel = <Trans id="infobubble-onboarding-end-demo">End demo</Trans>;
const nextDemoActionLabel = <Trans id="infobubble-onboarding-next-demo-chat">Next demo: Live Chat</Trans>;

export const AnalyticsMoreEducationTip: FC<SandboxEducationTipProps> = (props) => {
  const {trackDataEvent} = useDataEventTracking();

  const onNextDemoClick = async (event: FrontClickEvent) => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_ANALYTICS_MORE_END, {});
    // Wait for the chat demo to be provisioned before archiving the analytics demo conversation.
    await props.onNextClick(event);
  };

  const nextDemoAction = {
    label: nextDemoActionLabel,
    onClick: onNextDemoClick,
  };

  const onEndDemoClick = (event: FrontClickEvent) => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_ANALYTICS_MORE_END, {});
    // Advance/finish the analytics demo, despite not moving on to the chat demo.
    props.onEndClick(event, {shouldAdvanceStep: true});
  };

  const endDemoAction = {
    label: endDemoActionLabel,
    onClick: onEndDemoClick,
  };

  const onLearnMoreClick = () => {
    openInBrowser('https://academy.front.com/app/learning_paths/dfd6953c-0cf6-4f8d-93ff-2ba18645def3');
  };

  return (
    <EducationTip
      anchor={props.anchor}
      position="left"
      isVisible={props.isVisible}
      isStandalone
      onRequestClose={noop}
      pillType={InfoBubblePillVariantsEnum.EDUCATION}
      title={<Trans id="infobubble-onboarding-analytics-more-title">And there is much more&hellip;</Trans>}
      stepCounter={generateStepText(2, 2)}
      onPrimaryClick={nextDemoAction.onClick}
      primaryActionLabel={nextDemoAction.label}
      secondaryAction={endDemoAction}
      dataObjectName={OnboardingDataEventObject.DEMO_ANALYTICS_MORE}
    >
      <Trans id="infobubble-onboarding-analytics-more-description">
        Set up and customize analytics by team, channel, account, time frame, and more. You can schedule
        recurring email reports like this one, or view and filter in Front anytime.{' '}
        <StyledInlineLinkButton onClick={onLearnMoreClick}>
          Learn more <StyledExternalIcon name="externalLinkSmall" />
        </StyledInlineLinkButton>
      </Trans>
    </EducationTip>
  );
};
